import { FC, RefObject, MutableRefObject, useState, useEffect } from 'react';
import { Grid, Box, SelectChangeEvent, Tooltip, TextField, Link, Typography } from '@mui/material';
import styles from './DocumentBuilder.module.css';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  DocumentType,
  Page,
  clearSelectedItem,
  selectDocumentType,
  selectInitialDocument,
  selectSalesforceIds,
  selectSavingMethod,
  updateTemplateJsonSalesforceIds,
} from 'features/document/documentSlice';
import PageBuilder from './components/PageBuilder/PageBuilder';
import AddPage from './components/PageBuilder/components/AddPage/AddPage';
import {
  resetQuote,
  selectIsSalesforceAuth,
  selectQuotes,
  setQuotes,
  setQuote,
  selectOpportunities,
  setOpportunities,
  setLoan,
  setLoans,
  selectLoans,
  setContract,
  resetContract,
} from 'features/salesforce/salesforceSlice';
import { DropProvider } from './components/DropContext';
import ActionableSelect, { EDIT_OPTIONS_VALUE } from 'components/ActionableSelect/ActionableSelect';
import {
  parseContracts,
  parseOpportunityToSelectOption,
  parseOpportunitiesToSelectOption,
  parseQuotesToSelectOption,
  parseLoansToSelectOption,
} from 'utils/parseOpportunitiesAndQuotes';
import {
  useLazyGetOpportunityByIdQuery,
  useGetAllOpportunitiesQuery,
  useGetContractsByOpportunityIdQuery,
  useGetQuotesByOpportunityIdQuery,
  useGetAllLoansQuery,
  useLazyGetLoanByIdQuery,
} from 'features/salesforce/salesforceApi';
import OpportunitiesAutocomplete from './components/PageBuilder/components/OpportunitiesAutocomplete/OpportunitiesAutocomplete';
import LoansAutocomplete from './components/PageBuilder/components/LoansAutocomplete/LoansAutocomplete';
import ThumbnailSidebar from './components/PageBuilder/components/ThumbnailSidebar/ThumbnailSidebar';
import { openInNewTab } from 'utils/openInNewTab';
import { ActionableSelectOption } from 'components/ActionableSelect/ActionableSelect';
import { QueryBuilder } from 'utils/formatQueryParams';
import { debounce } from 'lodash';
import { useParams } from 'react-router-dom';
import { Launch } from '@mui/icons-material';
import { capitalizeFirstLetter } from 'utils/stringUtils';
interface Props {
  pages: Page[];
  pagesRef: MutableRefObject<RefObject<HTMLDivElement>[]>;
  zoom: number;
  thumbnailPagesRef: MutableRefObject<RefObject<HTMLDivElement>[]>;
  scrollToPage: (pageIndex: number, fromThumbnail: boolean) => void;
}

const DocumentBuilder: FC<Props> = ({ pages, pagesRef, zoom, thumbnailPagesRef, scrollToPage }) => {
  const dispatch = useAppDispatch();
  const selector = useAppSelector;
  const { id } = useParams();

  const isSalesforceAuth = selector(selectIsSalesforceAuth);
  const salesforceBaseUrl = selector(state => state.auth.salesforceBaseUrl);
  const documentType = selector(selectDocumentType);
  const salesforceIds = selector(selectSalesforceIds);
  const opportunities = selector(selectOpportunities);
  const quotes = selector(selectQuotes);
  const loans = selector(selectLoans);
  const savingMethod = selector(selectSavingMethod);
  const initialDocument = useAppSelector(selectInitialDocument);

  const parsedOpportunities = parseOpportunitiesToSelectOption(opportunities);
  const parsedQuotes = parseQuotesToSelectOption(quotes);
  const parsedLoans = parseLoansToSelectOption(loans);

  const isTemplate = savingMethod === 'TEMPLATE';

  const [query, setQuery] = useState('');
  const [selectedOpportunity, setSelectedOpportunity] = useState<ActionableSelectOption | null>(null);
  const [selectedLoan, setSelectedLoan] = useState<ActionableSelectOption | null>(null);
  const [activeOpportunity, setActiveOpportunity] = useState<string>('');
  const [activeQuote, setActiveQuote] = useState<string>('');
  const [activeContract, setActiveContract] = useState<string>('');
  const [activeLoan, setActiveLoan] = useState<string>('');

  const showOpportunitiesAutocomplete = isSalesforceAuth && !isTemplate && documentType !== DocumentType.PRECLOSING;
  const showQuotesAutocomplete = showOpportunitiesAutocomplete && documentType === DocumentType.QUOTE;
  const showContractsAutocomplete = showOpportunitiesAutocomplete && documentType === DocumentType.CONTRACT;
  const showLoansAutocomplete = isSalesforceAuth && !isTemplate && documentType === DocumentType.PRECLOSING;

  const builder = new QueryBuilder();
  const queryString = builder.setQueryLimit({ value: 4 });
  if (query) {
    builder.setQueryParam({ field: 'name', value: `${query}` });
  }

  const { data: dataOpportunities, isFetching: areOpportunitiesLoading } = useGetAllOpportunitiesQuery(
    queryString.build(),
    {
      skip: !isSalesforceAuth || isTemplate || !showOpportunitiesAutocomplete,
    }
  );

  const { data: dataLoans, isFetching: areLoansLoading } = useGetAllLoansQuery(queryString.build(), {
    skip: !isSalesforceAuth || isTemplate || !showLoansAutocomplete,
  });

  const [fetchOpportunity] = useLazyGetOpportunityByIdQuery();
  const [fetchLoan] = useLazyGetLoanByIdQuery();

  const {
    currentData: dataQuotes,
    refetch,
    isFetching,
  } = useGetQuotesByOpportunityIdQuery(activeOpportunity, {
    skip: (!isSalesforceAuth && activeOpportunity === '') || !showQuotesAutocomplete || isTemplate,
  });

  const {
    refetch: refetchContracts,
    isFetching: isFetchingContracts,
    currentData: dataContracts,
  } = useGetContractsByOpportunityIdQuery(activeOpportunity, {
    skip: !isSalesforceAuth || activeOpportunity === '' || !showContractsAutocomplete || isTemplate,
  });

  const isQuoteSelectDisabled = activeOpportunity === '' || activeOpportunity === EDIT_OPTIONS_VALUE || isFetching;
  const isContractSelectDisabled =
    activeOpportunity === '' || activeOpportunity === EDIT_OPTIONS_VALUE || isFetchingContracts;

  const isSidebarOpen = selector(state => state.document.isSidebarOpen);
  const showEditOptions = !!salesforceBaseUrl;
  const documentTypeString = capitalizeFirstLetter(documentType);

  const createOpportunityOption = {
    id: 'CreateOpportunityInCRM',
    label: 'Create Opportunity in CRM',
    onClick: () => {
      openInNewTab(`${salesforceBaseUrl}/lightning/o/Opportunity/new`);
    },
  };

  const debouncedFetchResults = debounce(searchQuery => {
    setQuery(searchQuery);
  }, 500);

  const handleSearchChange = (value: string) => {
    debouncedFetchResults(value);
  };

  const handleLoanSearchChange = (value: string) => {
    debouncedFetchResults(value);
    if (!value) {
      setActiveLoan('');
      setSelectedLoan(null);
    }
  };

  const handleClearSelection = () => {
    dispatch(clearSelectedItem({}));
  };

  const handleOnChangeOpportunity = (value: ActionableSelectOption) => {
    setSelectedOpportunity(value);
    setActiveOpportunity(value.id);
    dispatch(
      updateTemplateJsonSalesforceIds({
        opportunityId: value.id,
        quoteId: '',
        contractId: '',
        loandId: '',
      })
    );
  };

  const handleOnChangeLoan = (value: ActionableSelectOption) => {
    setSelectedLoan(value);
    setActiveLoan(value.id);

    dispatch(
      updateTemplateJsonSalesforceIds({
        opportunityId: '',
        quoteId: '',
        contractId: '',
        loanId: value.id,
      })
    );
  };

  const handleOnChangeQuote = (event: SelectChangeEvent) => {
    const newQuote = event.target.value;
    if (newQuote === EDIT_OPTIONS_VALUE) {
      openInNewTab(
        `${salesforceBaseUrl}/lightning/o/Quote/new?nooverride=true&defaultFieldValues=OpportunityId%3D${activeOpportunity}`
      );
    } else {
      setActiveQuote(event.target.value);
      dispatch(
        updateTemplateJsonSalesforceIds({
          opportunityId: activeOpportunity,
          quoteId: event.target.value,
          contractId: '',
          loandId: '',
        })
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchOpportunity(activeOpportunity);
      const parseOpp = parseOpportunityToSelectOption(data?.data?.data);
      setSelectedOpportunity(parseOpp);
    };

    if (activeOpportunity && !selectedOpportunity) fetchData();
  }, [activeOpportunity]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchLoan(activeLoan);
      dispatch(setLoan(data?.data?.data));
    };

    if (selectedLoan && selectedLoan.id) fetchData();
  }, [selectedLoan]);

  useEffect(() => {
    if (isTemplate) {
      dispatch(
        updateTemplateJsonSalesforceIds({
          opportunityId: '',
          quoteId: '',
          contractId: '',
          loandId: '',
        })
      );
      dispatch(resetQuote());
      dispatch(resetContract());
    }
  }, [isTemplate]);

  useEffect(() => {
    if (showQuotesAutocomplete && !id) {
      setActiveQuote('');
      dispatch(resetQuote());
    }

    if (showContractsAutocomplete && !id) {
      setActiveContract('');
      dispatch(resetContract());
    }

    if (activeOpportunity !== '') {
      showQuotesAutocomplete && refetch();
      showContractsAutocomplete && refetchContracts();
    }
  }, [activeOpportunity]);

  useEffect(() => {
    dispatch(setQuotes(dataQuotes?.data || []));
  }, [dataQuotes]);

  useEffect(() => {
    const newActiveQuote = quotes.find(quote => quote.Id === activeQuote);
    if (newActiveQuote) {
      dispatch(setQuote(newActiveQuote));
    }
  }, [activeQuote, quotes, initialDocument]);

  useEffect(() => {
    const newActiveLoan = parsedLoans.find(loan => loan.id === activeLoan);
    if (newActiveLoan) {
      setSelectedLoan(newActiveLoan);
    }
  }, [activeLoan, loans]);

  useEffect(() => {
    if (dataContracts) {
      const { data } = dataContracts;
      dispatch(setContract(parseContracts(data[0])));
      setActiveContract(data[0].ContractNumber);
      dispatch(
        updateTemplateJsonSalesforceIds({
          opportunityId: activeOpportunity,
          quoteId: '',
          contractId: data[0].ContractNumber,
          loandId: '',
        })
      );
    }
  }, [dataContracts, initialDocument]);

  useEffect(() => {
    if (isSalesforceAuth && dataOpportunities?.data) {
      dispatch(setOpportunities(dataOpportunities.data));
    }
  }, [dataOpportunities]);

  useEffect(() => {
    if (isSalesforceAuth && dataLoans?.data) {
      dispatch(setLoans(dataLoans.data));
    }
  }, [dataLoans]);

  useEffect(() => {
    const { opportunityId, quoteId, contractId, loanId } = salesforceIds;
    setActiveOpportunity(opportunityId || '');
    setActiveQuote(quoteId || '');
    setActiveContract(contractId || '');
    setActiveLoan(loanId || '');
  }, [salesforceIds]);

  return (
    <Grid className={styles.workspace} onClick={handleClearSelection}>
      {isSidebarOpen && (
        <ThumbnailSidebar pages={pages} thumbnailPagesRef={thumbnailPagesRef} scrollToPage={scrollToPage} />
      )}
      <Grid
        id={'document-window'}
        container
        className={`${styles.container} ${isSidebarOpen ? '' : styles.containerShift}}`}
        justifyContent='center'
        direction='column'
        alignContent='center'
        gap={2}
      >
        {showLoansAutocomplete && (
          <Box alignItems={'center'} display={'flex'} gap={'16px'}>
            <LoansAutocomplete
              areLoansLoading={areLoansLoading}
              selectedLoan={selectedLoan}
              parsedLoans={parsedLoans}
              handleSearchChange={handleLoanSearchChange}
              handleOnChangeLoan={handleOnChangeLoan}
            />
          </Box>
        )}
        {showOpportunitiesAutocomplete && (
          <Box alignItems={'center'} display={'flex'} gap={'16px'}>
            <OpportunitiesAutocomplete
              areOpportunitiesLoading={areOpportunitiesLoading}
              selectedOpportunity={selectedOpportunity}
              parsedOpportunities={parsedOpportunities}
              createOpportunityOption={createOpportunityOption}
              handleSearchChange={handleSearchChange}
              handleOnChangeOpportunity={handleOnChangeOpportunity}
            />
            {showQuotesAutocomplete && (
              <Box alignItems={'center'} display={'flex'} gap={'16px'}>
                <ActionableSelect
                  inputLabel='Quote Name'
                  disabled={isQuoteSelectDisabled}
                  options={parsedQuotes}
                  editOptions={showEditOptions}
                  editOptionsLabel='Create Quote in CRM'
                  value={activeQuote}
                  onChange={handleOnChangeQuote}
                  tooltipTitle='To make this field active, you need
                to select a Salesforce Opportunity.'
                />
              </Box>
            )}
            {showContractsAutocomplete && (
              <Box alignItems={'center'} display={'flex'} gap={'16px'}>
                {!activeContract && !isContractSelectDisabled && showEditOptions ? (
                  <Box alignItems='center' display='flex'>
                    <Box marginRight='5px'>
                      <Launch color='primary' />
                    </Box>
                    <Link
                      href={`${salesforceBaseUrl}/lightning/r/Opportunity/${activeOpportunity}/edit?`}
                      target='_blank'
                      style={{
                        fontSize: '16px',
                        fontWeight: '600',
                      }}
                    >
                      Assign Contract In CRM
                    </Link>
                  </Box>
                ) : (
                  <Tooltip
                    title='To fill in this field, you need
                to select a Salesforce Opportunity.'
                    arrow
                    placement='top'
                  >
                    <TextField
                      className={styles.contractTextField}
                      label='Contract Name'
                      disabled={isContractSelectDisabled || !activeContract}
                      value={activeContract}
                      InputProps={{
                        readOnly: true,
                        className: styles.contractOutlinedTextField,
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        )}

        {(showContractsAutocomplete || showQuotesAutocomplete) && (
          <Box width='477px'>
            <Typography id='modal-modal-title' variant='subtitle1' fontSize='12px' fontStyle='italic'>
              {`Select an Opportunity to pick a ${documentTypeString} and access its variables values. If no associated ${documentTypeString} exist, you can create one through Salesforce.`}
            </Typography>
          </Box>
        )}

        {pages.map((page: Page, index) => (
          <Grid
            item
            key={page.id}
            ref={pagesRef.current[index]}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            <DropProvider>
              <PageBuilder content={page} zoom={zoom} pageNumber={index + 1} totalPages={pages.length} />
            </DropProvider>
            <div className={styles.addPageContainer}>
              <AddPage previousPageIndex={index} />
            </div>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};
export default DocumentBuilder;
